import { Capacitor } from '@capacitor/core';
import { QueryClient } from '@tanstack/react-query';

/**
 * Build a QueryClient with the app's shared defaults. Used for the singleton
 * below and for the admin panel's per-org isolated client (`AdminOrgScope`).
 */
export function makeQueryClient(): QueryClient {
  // WKWebView can report offline while native transport still reaches the API.
  // Attempt native requests and let bounded transport/retry failures settle them.
  const networkMode = Capacitor.isNativePlatform() ? 'always' : 'online';
  return new QueryClient({
    defaultOptions: {
      mutations: { networkMode },
      queries: {
        networkMode,
        refetchOnReconnect: true,
        staleTime: 60 * 1000,
        retry: (failureCount, error: unknown) => {
          const httpError = error as {
            status?: number;
            response?: { status?: number };
          } | null;
          const status = httpError?.status ?? httpError?.response?.status;
          if (status === 401 || status === 403) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
        // A query that FAILED with no data is not re-run just because a
        // component mounted. With the default (true), any page that shows a
        // skeleton while loading and mounts children reading the same query
        // loops: error -> children mount -> refetch -> loading -> skeleton
        // unmounts them -> error. On 2026-10-09 the calendar did that ~50
        // times a second for a new org with no plan, ~3,000 requests a minute
        // from one tab, and throttled a production API machine. Failures are
        // re-run by the page's Reload (StateError), an invalidation, or a new
        // mount once the cached failure is garbage-collected.
        retryOnMount: false,
      },
    },
  });
}

export const queryClient = makeQueryClient();
